import React, { FC, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { BasicContainer, FlexCol, FlexDynamic, FlexRow, H4Text, Loader } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { publishPurchaseFlowEvent } from '@blocs.marketing-events';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { CompareFeatures } from './CompareFeatures';
import { PlanSm as PlanSmV1 } from './PlanSm';
import { PlanSmV2 } from './PlanSmV2';
import { Plan } from './Plan';
import { SelectPlanHead } from './SelectPlanHead';
import { PlanLoader } from './PlanLoader';
import { PlanSelectionProps } from './types';
import { MediaPurchase } from '../enums/plans';
import { PlanType } from '../usePlans';
import { PaywallEvents } from '../enums/events';
import { PaywallLocation } from '../enums/PaywallLocation';
import { RegistrationUpsell } from './RegistrationUpsell';
import { PaywallBanner } from './PaywallBanner';
import { OpportunityCostExperimentGroups, PriceFramingLanguageExperimentGroups } from '../enums/experimentGroups';
import { usePaywallExperiments } from '../usePaywallExperiments';
import { TestimonialCarousel } from './TestimonialCarousel';

export const PlanSelectionWrapper: FC<PlanSelectionProps> = ({
  onNext,
  isAnnual,
  plans,
  plansError,
  setIsAnnual,
  selectedPlan,
  setSelectedPlan,
  plansLoading,
  countryCode,
  getTitle = () => null,
  lessMd,
  onBack,
  startingStep,
  paywallLocation,
  isModal,
  onCompletePurchase,
  completePurchaseLoading,
  completePurchaseError,
  onPaymentCompleteRedirect = () => null,
  enableAnnualRenewal,
  isTrialEligible,
  isReactivate,
  hideIsAnnualToggle,
}) => {
  const { t } = useTranslation();
  const opportunityCostExperiment = useFeatureExperiment(FEATURE_FLAGS.EXPERIMENT_OPPORTUNITY_COST);
  const priceFramingLanguageExperiment = useFeatureExperiment(FEATURE_FLAGS.EXPERIMENT_PRICE_FRAMING_LANGUAGE);
  const { bannerIconColor, bannerBgColor } = usePaywallExperiments();
  const isLoading = plansLoading || completePurchaseLoading || plans.length === 0;
  const wrapperClasses = classNames('cn_w-full cn_atom_py-2', {
    'cn_atom_px-2': lessMd,
    'cn_atom_px-6': !lessMd,
  });

  useEffect(() => {
    if (plans.length === 0) return;

    const isMediaPurchase = selectedPlan?.billingCode === MediaPurchase.alacarte;
    const purchaseItems = plans.map((plan: PlanType) => ({
      name: `${plan.title} ${plan.term.code} ${plan.canRenew ? 'Renewal' : ''}`,
      code: `${plan.billingCode}${plan.canRenew ? '_renewal' : ''}`,
      price: Number(plan.price),
      currency: { code: plan.currencyCode },
    }));

    publishPurchaseFlowEvent(PaywallEvents.viewItemList, {
      location: paywallLocation,
      purchase: {
        items: isMediaPurchase ? purchaseItems : [],
        subscriptions: !isMediaPurchase ? purchaseItems : [],
      },
    });
  }, [plans, paywallLocation, selectedPlan]);

  const PlanSm = useMemo(() => (paywallLocation === PaywallLocation.SIGNUP ? PlanSmV2 : PlanSmV1), [paywallLocation]);

  return (
    <Loader
      loading={isLoading}
      LoaderComponent={<PlanLoader />}
      error={plansError && t('common:globalError.genericUnexpected')}
    >
      <BasicContainer
        data-testid="paywall-selection-wrapper"
        bgColor="card"
        borderRadius="md"
        className={wrapperClasses}
      >
        <BasicContainer mx={isModal ? '0' : '2'}>
          <RegistrationUpsell isRegistration={paywallLocation === PaywallLocation.SIGNUP} />
          <FlexCol justifyContent="center" alignItems="center" gap="8" w="full">
            <FlexRow justifyContent="space-between" alignItems="flex-start" w="full">
              {selectedPlan?.discountOfferText && (
                <FlexDynamic w="full" alignSelf="center" justifyContent="center" mdProps={{ hidden: true }}>
                  <H4Text fontWeight="bold" color="emphasis">
                    {t(selectedPlan.discountOfferText)}
                  </H4Text>
                </FlexDynamic>
              )}
              <SelectPlanHead
                t={t}
                isAnnual={isAnnual}
                setIsAnnual={setIsAnnual}
                lessMd={lessMd}
                getTitle={getTitle}
                paywallLocation={paywallLocation}
                isModal={isModal}
                enableAnnualRenewal={enableAnnualRenewal}
                isReactivate={isReactivate}
                hideIsAnnualToggle={hideIsAnnualToggle}
              />
              {opportunityCostExperiment && opportunityCostExperiment !== OpportunityCostExperimentGroups.Control && (
                <PaywallBanner
                  iconColor={bannerIconColor}
                  bgColor={bannerBgColor}
                  title="common:paywall.banner.title.opportunityCost"
                />
              )}
              {priceFramingLanguageExperiment &&
                priceFramingLanguageExperiment === PriceFramingLanguageExperimentGroups.VariantB && (
                  <PaywallBanner
                    title="common:paywall.banner.title.coffeePrice"
                    iconName="coffee"
                    data-testid="price-framing-banner"
                  />
                )}
              {lessMd ? (
                <PlanSm
                  onNext={onNext}
                  plans={plans}
                  isAnnual={isAnnual}
                  plansError={plansError}
                  plansLoading={plansLoading}
                  setIsAnnual={setIsAnnual}
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                  countryCode={countryCode}
                  getTitle={getTitle}
                  lessMd={lessMd}
                  onBack={onBack}
                  startingStep={startingStep}
                  onCompletePurchase={onCompletePurchase}
                  completePurchaseLoading={completePurchaseLoading}
                  completePurchaseError={completePurchaseError}
                  paywallLocation={paywallLocation}
                  onPaymentCompleteRedirect={onPaymentCompleteRedirect}
                />
              ) : (
                <Plan
                  onNext={onNext}
                  plans={plans}
                  isAnnual={isAnnual}
                  plansError={plansError}
                  plansLoading={plansLoading}
                  setIsAnnual={setIsAnnual}
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                  countryCode={countryCode}
                  getTitle={getTitle}
                  lessMd={lessMd}
                  startingStep={startingStep}
                  onCompletePurchase={onCompletePurchase}
                  completePurchaseLoading={completePurchaseLoading}
                  completePurchaseError={completePurchaseError}
                  paywallLocation={paywallLocation}
                  onPaymentCompleteRedirect={onPaymentCompleteRedirect}
                  isTrialEligible={isTrialEligible}
                />
              )}
            </FlexRow>
            {!lessMd && <CompareFeatures plans={plans} />}
            <TestimonialCarousel />
          </FlexCol>
        </BasicContainer>
      </BasicContainer>
    </Loader>
  );
};
